import {
    OrevePopup,
    OrevePopupTitle,
    OrevePopupContent,
} from "components/popup/OrevePopup.tsx"
import { CreditCard, CheckCircleOutline } from "@mui/icons-material"
import { Typography, TextField, MenuItem, Box } from "@mui/material"
import React, { useEffect, useState } from "react"
import { OreveButton } from "components/button/OreveButton.tsx"
import { useMmsService } from "services/mms/context/MmsServiceContext.tsx"
import { MmsServiceInterface } from "interfaces/mms-service.ts"
import ErrorIcon from "assets/svg/error-icon.svg"
import { UseRfidContext } from "layouts/rfid/context/provider"

export const RfidReactivateCard: React.FC = () => {
    const {
        fetchListRfid,
        currentCardSelected,
        setReactivateCard,
        reactivateCard,
        setCurrentCardSelected,
    } = UseRfidContext()
    const mmsService: MmsServiceInterface = useMmsService()
    const rfidService = mmsService.getRfidService()
    const rfidId = currentCardSelected != null ? currentCardSelected.id : ""
    const { sendRequest: updateStatusCard } =
        rfidService.useUpdateStatusRfidCard(rfidId)

    return (
        <OrevePopup
            open={reactivateCard}
            handleClose={() => {
                setReactivateCard(false)
                setCurrentCardSelected(null)
            }}
        >
            <OrevePopupTitle>
                <img src={ErrorIcon} alt={"error icon"} />
                <Typography
                    data-testid="popup-title"
                    variant="headlineSmall"
                    component="div"
                >
                    {`Êtes-vous certain de vouloir réactiver la carte n°${currentCardSelected?.rfidNumber} 
                        associée à ${currentCardSelected?.cardHolder} ?`}
                </Typography>
            </OrevePopupTitle>

            <OrevePopupContent>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "1rem",
                    }}
                >
                    <OreveButton
                        data-cy="dialog__rfid--cancel"
                        variant={"outlined"}
                        color={"primary"}
                        value="Annuler"
                        onClick={() => {
                            setReactivateCard(false)
                            setCurrentCardSelected(null)
                        }}
                    />
                    <OreveButton
                        data-cy="dialog__rfid--reactivate"
                        value="Réactiver"
                        onClick={() => {
                            setReactivateCard(false)
                            updateStatusCard({
                                rfidNumber: currentCardSelected?.rfidNumber,
                                cardHolder: currentCardSelected?.cardHolderId,
                                description: `RFID card ${rfidId}`,
                                active: true,
                                status: "enabled",
                                id: currentCardSelected?.id,
                            })
                                .then(() => fetchListRfid())
                                .then(() => {
                                    setCurrentCardSelected(null)
                                })
                        }}
                    />
                </Box>
            </OrevePopupContent>
        </OrevePopup>
    )
}

export const RfidDisableCard: React.FC = () => {
    const {
        currentCardSelected,
        setDisableCard,
        disableCard,
        setDisableCardConfirm,
        setCurrentCardSelected,
    } = UseRfidContext()
    const [success, setSuccess] = useState<boolean>(false)
    const [reason, setReason] = useState("default")
    const disableReason = [
        {
            dataCy: "dialog__reason--select",
            value: "default",
            label: "Sélectionnez un motif",
        },
        {
            dataCy: "dialog__reason--loss",
            value: "loss",
            label: "Perte",
        },
        {
            dataCy: "dialog__reason--theft",
            value: "theft",
            label: "Vol",
        },
        {
            dataCy: "dialog__reason--disallocation",
            value: "vehicle_disallocation",
            label: "Désatribution du véhicule",
        },
        {
            dataCy: "dialog__reason--deactivation",
            value: "driver_deactivation",
            label: "Conducteur désactivé",
        },
    ]
    const handleReasonChange = (
        event: React.ChangeEvent<{ value: unknown }>
    ) => {
        setReason(event.target.value as string)
    }

    useEffect(() => {
        setSuccess(false)
        setReason("default")
    }, [])

    const changeCardStatus = async () => {
        setSuccess(true)
        setDisableCard(false)
        setDisableCardConfirm(true)
    }

    return (
        <>
            <OrevePopup
                open={disableCard}
                handleClose={() => {
                    setDisableCard(false)
                    setReason("default")
                    setSuccess(false)
                    setCurrentCardSelected(null)
                }}
            >
                <OrevePopupTitle>
                    <img src={ErrorIcon} alt={"error-icon"} />
                    <Typography
                        data-testid="popup-title"
                        variant="headlineSmall"
                        component="div"
                    >
                        {`Êtes-vous certain de vouloir désactiver la carte n°${currentCardSelected?.rfidNumber} 
                        associée à ${currentCardSelected?.cardHolder} ?`}
                    </Typography>
                </OrevePopupTitle>
                <OrevePopupContent>
                    <Typography variant={"labelLarge"}>
                        Veuillez indiquer un motif de désactivation :
                    </Typography>
                </OrevePopupContent>
                <OrevePopupContent>
                    <TextField
                        id="filled-select-currency"
                        select
                        label="Motif"
                        variant="filled"
                        sx={{ width: "60%", textAlign: "left" }}
                        value={reason}
                        onChange={handleReasonChange}
                    >
                        {disableReason.map((option) => (
                            <MenuItem
                                data-cy={option.dataCy}
                                key={option.value}
                                value={option.value}
                                disabled={option.value === "default"}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </OrevePopupContent>
                <OrevePopupContent>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "1rem",
                        }}
                    >
                        <OreveButton
                            data-cy="dialog__reason--cancel"
                            variant={"outlined"}
                            color={"primary"}
                            value="Annuler"
                            onClick={() => {
                                setDisableCard(false)
                                setReason("default")
                                setSuccess(false)
                                setCurrentCardSelected(null)
                            }}
                        />
                        <OreveButton
                            data-cy="dialog__reason--disable"
                            value="Désactiver"
                            disabled={reason === "default"}
                            onClick={() => {
                                changeCardStatus()
                            }}
                        />
                    </Box>
                </OrevePopupContent>
            </OrevePopup>

            {success && currentCardSelected && (
                <RfidConfirmDisableCard
                    key={currentCardSelected.id}
                    rfidNumber={currentCardSelected.rfidNumber}
                    id={currentCardSelected.id}
                    reason={reason}
                />
            )}
        </>
    )
}

interface RfidConfirmDisableCardProps {
    rfidNumber: string
    id: string
    reason: string
}

const RfidConfirmDisableCard: React.FC<RfidConfirmDisableCardProps> = ({
    rfidNumber,
    id,
    reason,
}) => {
    const {
        fetchListRfid,
        currentCardSelected,
        disableCardConfirm,
        setDisableCardConfirm,
        setCurrentCardSelected,
    } = UseRfidContext()
    const mmsService: MmsServiceInterface = useMmsService()
    const rfidService = mmsService.getRfidService()
    const rfidId = currentCardSelected != null ? currentCardSelected.id : ""
    const { sendRequest: updateStatusCard } =
        rfidService.useUpdateStatusRfidCard(id)

    useEffect(() => {
        updateStatusCard({
            rfidNumber: currentCardSelected?.rfidNumber,
            cardHolderId: currentCardSelected?.cardHolderId,
            description: `RFID card ${rfidId}, ${reason}`,
            active: false,
            status: "suspended",
            id: currentCardSelected?.id,
        }).then()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <OrevePopup
            open={disableCardConfirm}
            handleClose={() => {
                setDisableCardConfirm(false)
                fetchListRfid().then(() => {
                    setCurrentCardSelected(null)
                })
            }}
        >
            <OrevePopupTitle>
                <CheckCircleOutline sx={{ color: "secondary.main" }} />
                <Typography variant="headlineSmall" component="div">
                    Carte désactivée avec succès
                </Typography>
            </OrevePopupTitle>
            <OrevePopupContent>
                <Typography
                    variant={"labelLarge"}
                >{`La carte n°${rfidNumber} de ${currentCardSelected?.cardHolder} a correctement été désactivée.`}</Typography>
            </OrevePopupContent>
            <OrevePopupContent>
                <OreveButton
                    value="Retour à la liste des cartes"
                    icon={CreditCard}
                    onClick={() => {
                        setDisableCardConfirm(false)
                        fetchListRfid().then(() => setCurrentCardSelected(null))
                    }}
                />
            </OrevePopupContent>
        </OrevePopup>
    )
}
